/**
 * Application Constants and Configuration
 * Contains all static data, configuration values, and predefined options
 * used throughout the application for consistency and maintainability
 */

// ============================================================================
// METRIC FORM BUILDER CONSTANTS
// ============================================================================

/**
 * JSON Response Information Content Schema
 * Defines the structure for sustainability item category forms
 */
export const JSonResponseInfoContent = {
  type: "object",
  title: "Sustainability Item Category",
  required: [],
  properties: {
    Remarks: {
      id: 1,
      type: "string",
      title: "Remarks",
    },
    "NIC Code": {
      id: 3,
      type: "integer",
      title: "Give Title",
    },
    "Main activity": {
      id: 2,
      type: "string",
      title: "Give Title",
    },
  },
  description: "",
};

// ============================================================================
// CATEGORY AND CLASSIFICATION CONSTANTS
// ============================================================================

/**
 * ESG Category List
 * Defines the main categories for Environmental, Social, and Governance metrics
 */
export const CategoryList = [
  {
    id: "1",
    name: "Environmental",
  },
  {
    id: "2",
    name: "Social",
  },
  {
    id: "3",
    name: "Governance",
  },
  {
    id: "4",
    name: "Reference Data",
  },
];

/**
 * Array of category names extracted from CategoryList
 * Used for dropdown menus and form validation
 */
export const categoryNameList = CategoryList.map(category => category.name);

/**
 * Approval Workflow Options
 * Defines different approval processes for metrics and policies
 */
export const ApprovalWorkFlowList = [
  {
    id: "1",
    name: "Default",
  },
  {
    id: "2",
    name: "1-Step Approval",
  },
  {
    id: "3",
    name: "2-Step Approval",
  },
];

// ============================================================================
// COLOR AND STYLING CONSTANTS
// ============================================================================

/**
 * Standard color palette for UI components
 * Used for buttons, badges, and status indicators
 */
export const Colors = ["secondary", "success", "warning", "danger", "primary"];

/**
 * Color mapping for different metric categories
 * Provides consistent visual representation across the application
 */
export const metricColor = {
  environment: "success",    // Green for environmental metrics
  general: "primary",        // Blue for general metrics
  social: "warning",         // Orange for social metrics
  governance: "secondary",   // Gray for governance metrics
  survey: "danger",          // Red for survey metrics
};

/**
 * Number of available colors in the palette
 * Used for cycling through colors in lists and charts
 */
export const numColors = Colors.length;

// ============================================================================
// FREQUENCY AND TIMING CONSTANTS
// ============================================================================

/**
 * Frequency Data Object for Metric Playground
 * Defines dropdown options for metric collection frequency
 */
export const FrequencyDataObj = {
  id: "frequency",
  name: "Drop Down",
  type: "dropdown",
  title: "Recommended Frequency",
  values: [
    {
      id: 0,
      key: 0,
      name: "Only Once",
    },
    {
      id: 1,
      key: 1,
      name: "Everyday",
    },
    {
      id: 2,
      key: 7,
      name: "Every week",
    },
    {
      id: 3,
      key: 30,
      name: "Every month",
    },
    {
      id: 4,
      key: -1,
      name: "Custom",
    },
  ],
  required: false,
};

/**
 * Frequency Count Mapping
 * Maps human-readable frequency names to their corresponding day counts
 */
export const FrequecyCountMap = {
  "Only Once": 0,
  "Everyday": 1,
  "Every week": 7,
  "Every month": 30,
};

// ============================================================================
// FORM SCHEMA OBJECTS
// ============================================================================

/**
 * Category Data Schema Object
 * Defines the dropdown configuration for category selection
 */
export const CategoryDataSchemaObj = {
  id: "category",
  name: "Drop Down",
  type: "dropdown",
  title: "Category",
  values: [...CategoryList],
  required: true,
  ismultiselect: false,
};

/**
 * Approval Workflow Data Schema Object
 * Defines the dropdown configuration for approval workflow selection
 */
export const ApprovalWorkFlowDataSchemaObj = {
  id: "approvalWorkflow",
  name: "Drop Down",
  type: "dropdown",
  title: "ApprovalWorkFlow",
  values: [...ApprovalWorkFlowList],
  required: true,
  ismultiselect: false,
};

/**
 * Framework Data Schema Object
 * Defines the dropdown configuration for framework selection
 */
export const FrameWorkDataSchemaObj = {
  id: "frameWork",
  name: "Drop Down",
  type: "dropdown",
  title: "frameWork",
  values: [],
  required: false,
  ismultiselect: true,
};

// ============================================================================
// FORM FIELD DEFINITIONS
// ============================================================================

/**
 * Field List for UI Form Builder
 * Defines all available input field types with their properties and icons
 */
export const FieldList = {
  text: {
    id: 1,
    name: "text",
    title: "Text Input",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
        />
      </svg>
    ),
  },
  integer: {
    id: 2,
    name: "integer",
    title: "Integer",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008Zm0 2.25h.008v.008H8.25V13.5Zm0 2.25h.008v.008H8.25v-.008Zm0 2.25h.008v.008H8.25V18Zm2.498-6.75h.007v.008h-.007v-.008Zm0 2.25h.007v.008h-.007V13.5Zm0 2.25h.007v.008h-.007v-.008Zm0 2.25h.007v.008h-.007V18Zm2.504-6.75h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V13.5Zm0 2.25h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V18Zm2.498-6.75h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V13.5ZM8.25 6h7.5v2.25h-7.5V6ZM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 0 0 2.25 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0 0 12 2.25Z"
        />
      </svg>
    ),
  },
  textarea: {
    id: 3,
    name: "textarea",
    title: "Text Area",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
        />
      </svg>
    ),
  },
  password: {
    id: 4,
    name: "password",
    title: "Password",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
        />
      </svg>
    ),
  },
  date: {
    id: 5,
    name: "date",
    title: "Date",

    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
        />
      </svg>
    ),
  },
  boolean: {
    id: 6,
    name: "boolean",
    title: "Boolean",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
        />
      </svg>
    ),
  },
  slider: {
    id: 7,
    name: "slider",
    title: "Percentage Slider",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m9 14.25 6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185ZM9.75 9h.008v.008H9.75V9Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm4.125 4.5h.008v.008h-.008V13.5Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
        />
      </svg>
    ),
  },
  dropdown: {
    id: 8,
    name: "dropdown",
    title: "Drop Down",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
        />
      </svg>
    ),
  },
  file: {
    id: 9,
    name: "file",
    title: "File Upload",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
        />
      </svg>
    ),
  },
  relation: {
    id: 10,
    name: "relation",
    title: "Relation",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
        />
      </svg>
    ),
  },

  link: {
    id: 11,
    name: "linkUrl",
    title: "linkUrl",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
        />
      </svg>
    ),
  },
  conditional: {
    id: 12,
    name: "conditional",
    title: "Conditional Field",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
        />
      </svg>
    ),
  },
  subheading: {
    id: 13,
    name: "subheading",
    title: "Subheading",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
        />
      </svg>
    ),
  },
  metricSelector: {
    id: 14,
    name: "metricSelector",
    title: "Metric Selector",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="size-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
        />
      </svg>
    ),
  },

};

// function defination info

export const functionDefInfo = function (formSchema) {
  // Define logic here
  // return value
};

// Check String is convertable to Function or Not

// Function to check if a string is convertible to a function

// export function isValidFunctionDefination(functionString) {
//   if (typeof functionString !== "string" || functionString.trim() === "") {
//     return false;
//   }

//   try {
//     const func = new Function("return (" + functionString + ")");
//     if (typeof func() === "function") {
//       return true;
//     }
//   } catch (e) {
//     console.log(e);
//     return false;
//   }

//   return false;
// }
export function escapeBackticks(str) {
  return str.replace(/`/g, "\\`");
}

export function isValidFunctionDefination(functionString) {
  if (typeof functionString !== "string" || functionString.trim() === "") {
    return false;
  }

  const escapedFunctionString = escapeBackticks(functionString);

  try {
    const func = new Function(`return (${escapedFunctionString})`);
    if (typeof func() === "function") {
      return true;
    }
  } catch (e) {
    return false;
  }

  return false;
}

export const functionStructureForFormula =
  "function (data){\n  // Write your logic Here.......\n  //return val;\n}";

export const CopyIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z"
    />
  </svg>
);

export const editIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    class="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
    />
  </svg>
);

export const ProgessColors = ["danger", "secondary", "warning", "success"];

export const defaultOrgId = 42;
